/**
 * Relewise Shopify app ships nested web components whose <input>, icon and
 * results list live inside sealed-but-open shadow roots. The app exposes no
 * attribute for the placeholder, and the internal CSS hides the magnifier
 * icon until focus. This shim:
 *   - sets + defends the placeholder attribute
 *   - injects CSS into BOTH shadow roots (overlay + search-bar) so width,
 *     icon visibility, and the results grid stay under our control
 */
(function () {
  /* Translatable copy is bridged in from the theme locale files via
     snippets/relewise-search-strings.liquid, which sets window.RelewiseStrings
     from the `general.relewise_search.*` keys. Each lookup falls back to the
     original Swedish string so the overlay still reads correctly if the
     bridge snippet is ever absent. Edit the copy in Translate & Adapt or
     "Edit default theme content", not here. */
  var S = (window.RelewiseStrings && typeof window.RelewiseStrings === 'object') ? window.RelewiseStrings : {};
  var PLACEHOLDER = S.placeholder || 'Sök efter namn, färg, typ, aktivitet och mer...';

  /* CSS that goes inside the <relewise-product-search-overlay> shadow root.
     The results dropdown is a SEPARATE custom element
     (<relewise-product-search-overlay-results>) with its own shadow root,
     so grid rules there won't apply here — see RESULTS_ROOT_CSS below. */
  var OVERLAY_ROOT_CSS = [
    'relewise-search-bar {',
    '  width: 100% !important;',
    '  max-width: none !important;',
    '  margin: 0 !important;',
    '  padding: 0 !important;',
    '  display: block !important;',
    '  box-sizing: border-box !important;',
    '}',
    'relewise-product-search-overlay-results {',
    '  width: 100% !important;',
    '  max-width: none !important;',
    '  display: block !important;',
    '}'
  ].join('\n');

  /* CSS injected into <relewise-product-search-overlay-results>\'s shadow
     root. Actual structure (verified via debug log):
       <div class="rw-result-container" part="overlay">
         <div class="rw-selected-result">  — term echo "form" + magnifier
         <div class="rw-selected-result">  — .rw-title "Products"
         <div class="rw-selected-result">  — .rw-product-item-container > <relewise-product-search-overlay-product>
         ... (one .rw-selected-result sibling per product; capped to 4 in CSS)
         <div class="rw-selected-result">  — .rw-title "Categories"
         <div class="rw-selected-result">  — .rw-product-item-container > <relewise-...-category>  (x3)
         <div class="rw-selected-result">  — "Show all results" row
       </div>
     Approach: turn .rw-result-container into a CSS grid. Default each
     .rw-selected-result to full-width (grid-column: 1 / -1), then use
     :has() to pick only the product / category rows and have them
     occupy a single column — they flow side-by-side automatically. */
  var RESULTS_ROOT_CSS = [
    '.rw-result-container {',
    '  display: grid !important;',
    /* minmax(0, 1fr) — without the 0 minimum, columns expand to fit the
       card's min-content (size buttons row), pushing the grid wider than
       the viewport on mobile. The 0 lets cards shrink instead. */
    '  grid-template-columns: repeat(4, minmax(0, 1fr)) !important;',
    '  gap: 8px 12px !important;',
    '  padding: 8px 24px 16px !important;',
    '  margin: 0 !important;',
    '  box-sizing: border-box !important;',
    '  align-items: start !important;',
    '  width: 100% !important;',
    '  max-width: 100% !important;',
    '  min-width: 0 !important;',
    /* Cap height so long result lists scroll inside the dropdown instead
       of extending past the viewport. overscroll-behavior: contain stops
       wheel events from bubbling and moving the page underneath. */
    '  max-height: calc(100vh - 160px) !important;',
    '  overflow-y: auto !important;',
    '  overscroll-behavior: contain !important;',
    '  position: relative !important;',
    '  border: none !important;',
    '}',
    /* Override Relewise\'s default .rw-border (full outer outline) so the
       absolute-positioned button at top-right doesn\'t overlap a divider. */
    '.rw-result-container.rw-border { border: none !important; }',
    /* Responsive grid (desktop base is 4 columns) */
    '@media (max-width: 850px) { .rw-result-container { grid-template-columns: repeat(3, minmax(0, 1fr)) !important; } }',
    '@media (max-width: 749px) { .rw-result-container { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; padding: 4px 12px 12px !important; gap: 4px !important; } }',
    /* Default: every row spans the full grid width */
    '.rw-result-container > .rw-selected-result {',
    '  grid-column: 1 / -1 !important;',
    '  padding: 0 !important;',
    '  margin: 0 !important;',
    '  width: 100% !important;',
    '}',
    /* Product / category rows: one column each, so they tile into the grid */
    '.rw-result-container > .rw-selected-result:has(relewise-product-search-overlay-product),',
    '.rw-result-container > .rw-selected-result:has(relewise-product-search-overlay-product-category) {',
    '  grid-column: span 1 !important;',
    '  padding-top: 4px !important;',
    '}',
    /* Fallback cap at 4 products. The primary control is the overlay's
       `number-of-products="4"` attribute set in processOverlay(); this rule
       defends against Relewise's default (5) winning a race before the
       attribute applies. Hide any product row that has 4 product-row
       siblings before it — i.e. the 5th onward. The `~` combinator ignores
       the title/category rows between product rows, so this survives
       Relewise reordering. */
    '.rw-result-container > .rw-selected-result:has(relewise-product-search-overlay-product)' +
      ' ~ .rw-selected-result:has(relewise-product-search-overlay-product)' +
      ' ~ .rw-selected-result:has(relewise-product-search-overlay-product)' +
      ' ~ .rw-selected-result:has(relewise-product-search-overlay-product)' +
      ' ~ .rw-selected-result:has(relewise-product-search-overlay-product) {',
    '  display: none !important;',
    '}',
    /* Item container fills the grid cell */
    '.rw-product-item-container {',
    '  width: 100% !important;',
    '  display: block !important;',
    '  padding: 0 !important;',
    '  margin: 0 !important;',
    '  min-width: 0 !important;',
    '}',
    /* The custom elements rendering the card fill the cell too */
    'relewise-product-search-overlay-product,',
    'relewise-product-search-overlay-product-category {',
    '  display: block !important;',
    '  width: 100% !important;',
    '  min-width: 0 !important;',
    '}',
    /* Section titles ("Products", "Categories") — bumped to brand
       --t-h4 (16px) sans, weight 600, ink colour for prominence. */
    '.rw-title {',
    '  font-family: "Euclid Circular A", "Inter", -apple-system, sans-serif !important;',
    '  font-size: 16px !important;',
    '  text-transform: uppercase !important;',
    '  letter-spacing: 0.04em !important;',
    '  font-weight: 600 !important;',
    '  color: #000 !important;',
    '  margin: 0 !important;',
    '  padding: 0 !important;',
    '  border: none !important;',
    '}',
    /* No dividers anywhere inside the dropdown. */
    '.rw-result-container, .rw-result-container * {',
    '  border-top: none !important;',
    '  border-bottom: none !important;',
    '}',
    /* Hide the duplicated query term echo at the top — already visible
       in the search bar above. Identified by <relewise-search-icon>. */
    '.rw-result-container > .rw-selected-result:has(relewise-search-icon) {',
    '  display: none !important;',
    '}',
    /* Place the "Show all results (N)" button in the SAME grid row as
       the Products title. Both items get align-self: center so their
       boxes share a row and centre on the same Y baseline — no more
       pixel-fudging with absolute top/right. Identified by
       <relewise-arrow-up-icon>. */
    '.rw-result-container > .rw-selected-result:has(relewise-arrow-up-icon) {',
    '  position: static !important;',
    '  top: auto !important;',
    '  right: auto !important;',
    '  grid-row: 1 !important;',
    '  grid-column: -2 / -1 !important;',
    '  align-self: center !important;',
    '  justify-self: end !important;',
    '  margin: 0 !important;',
    '  padding: 0 !important;',
    '  border: none !important;',
    '  z-index: 2 !important;',
    '  display: block !important;',
    '  width: auto !important;',
    '}',
    /* Pin the FIRST title row ("Produkter") to grid-row 1 so the
       "Visa alla resultat" button shares that row. Content-based
       selector (:has(.rw-title)) survives any child-order changes
       Relewise makes; the sibling combinator excludes subsequent
       title rows (e.g. "Kategorier") so they flow naturally lower
       in the grid. align-self:center keeps both row 1 items on a
       single Y baseline. margin-bottom controls the breathing
       room between the title row and the products grid below. */
    '.rw-result-container > .rw-selected-result:has(.rw-title) {',
    '  grid-row: 1 !important;',
    '  align-self: center !important;',
    '  margin-bottom: 4px !important;',
    '}',
    '.rw-result-container > .rw-selected-result:has(.rw-title) ~ .rw-selected-result:has(.rw-title) {',
    '  grid-row: auto !important;',
    '  margin-bottom: 0 !important;',
    '}',
    '.rw-result-container > .rw-selected-result:has(relewise-arrow-up-icon) .rw-item-container {',
    '  display: inline-flex !important;',
    '  align-items: center !important;',
    '  justify-content: center !important;',
    '  gap: 0 !important;',
    '  padding: 7px 14px !important;',
    '  background: #000 !important;',
    '  color: #fff !important;',
    '  border-radius: 3px !important;',
    '  cursor: pointer !important;',
    '  transition: background 180ms ease !important;',
    '  width: auto !important;',
    '  font-size: 12px !important;',
    '  font-weight: 500 !important;',
    '  line-height: 1.4 !important;',
    '}',
    '.rw-result-container > .rw-selected-result:has(relewise-arrow-up-icon) .rw-item-container:hover {',
    '  background: #1C1714 !important;',
    '}',
    '.rw-result-container > .rw-selected-result:has(relewise-arrow-up-icon) .rw-item {',
    '  color: inherit !important;',
    '  font-size: inherit !important;',
    '  font-weight: 500 !important;',
    '  text-transform: none !important;',
    '  letter-spacing: 0.02em !important;',
    '}',
    /* Drop the arrow icon — button text is enough */
    '.rw-result-container > .rw-selected-result:has(relewise-arrow-up-icon) .rw-icon {',
    '  display: none !important;',
    '}',
    /* Category rows: plain underlined text, side by side. No box. */
    '.rw-result-container > .rw-selected-result:has(relewise-product-search-overlay-product-category) {',
    '  grid-column: span 1 !important;',
    '  padding: 4px 0 !important;',
    '  margin: 0 !important;',
    '  border: none !important;',
    '  border-radius: 0 !important;',
    '  background: transparent !important;',
    '}',
    'relewise-product-search-overlay-product-category {',
    '  display: block !important;',
    '  font-size: 16px !important;',
    '  font-weight: 500 !important;',
    '  color: #000 !important;',
    '  letter-spacing: -0.01em !important;',
    '}',
    /* Mobile: shrink the title row + "Visa alla resultat" pill so they
       sit comfortably side-by-side in the narrow viewport without the
       button getting clipped. Smaller category text matches the
       tightened scale. */
    '@media (max-width: 749px) {',
    '  .rw-title {',
    '    font-size: 11px !important;',
    '    letter-spacing: 0.06em !important;',
    '  }',
    '  .rw-result-container > .rw-selected-result:has(relewise-arrow-up-icon) .rw-item-container {',
    '    padding: 5px 8px !important;',
    '    font-size: 10px !important;',
    '    white-space: nowrap !important;',
    '  }',
    '  relewise-product-search-overlay-product-category {',
    '    font-size: 13px !important;',
    '  }',
    '}'
  ].join('\n');

  /* CSS injected into each <relewise-product-search-overlay-product-category>
     shadow root. Plain underlined text — no box, no background. */
  var CATEGORY_ROOT_CSS = [
    ':host { display: block !important; width: 100% !important; }',
    'a, .rw-category, [class*="category"] {',
    '  display: inline-block !important;',
    '  font-family: "Euclid Circular A", "Inter", -apple-system, sans-serif !important;',
    '  font-size: 14px !important;',
    '  font-weight: 500 !important;',
    '  color: #000 !important;',
    '  text-decoration: underline !important;',
    '  text-underline-offset: 4px !important;',
    '  text-decoration-thickness: 1px !important;',
    '  text-decoration-color: #D1D3D4 !important;',
    '  padding: 4px 0 !important;',
    '  letter-spacing: -0.01em !important;',
    '  cursor: pointer !important;',
    '  transition: text-decoration-color 180ms ease !important;',
    '}',
    'a:hover, [class*="category"]:hover {',
    '  text-decoration-color: #000 !important;',
    '  text-decoration-thickness: 2px !important;',
    '}'
  ].join('\n');

  /* CSS injected into each <relewise-product-search-overlay-product>
     shadow root. The product card HTML lives inside this shadow, but
     it\'s served from the live theme so the natural-height image was
     producing inconsistent card heights. Force a fixed 3:4 aspect
     ratio on every card image so all cards render uniformly. */
  var PRODUCT_ROOT_CSS = [
    ':host { display: block !important; width: 100% !important; }',
    /* The card's linked stylesheet (section-tabbed-collection-relewise.css)
       does NOT reliably take effect inside this product element's shadow
       root, so without these rules the two <img> tags fall back to in-flow,
       natural-height rendering and STACK — producing a hugely over-tall
       card. Replicate the theme's proven 3:4 mechanism self-sufficiently:
       a width-derived padding-bottom box (Safari-safe — see the source
       stylesheet's comment) with both images pulled out of flow so they
       can't drive the box height. */
    '.tabbed-product-card__media {',
    '  position: relative !important;',
    '  width: 100% !important;',
    '  height: 0 !important;',
    '  padding-bottom: 133.333% !important;', /* 3:4 portrait */
    '  aspect-ratio: auto !important;',       /* defer to padding-bottom */
    '  overflow: hidden !important;',
    '}',
    '.tabbed-product-card__img,',
    '.tabbed-product-card__img--primary,',
    '.tabbed-product-card__img--hover {',
    '  position: absolute !important;',
    '  top: 0 !important;',
    '  left: 0 !important;',
    '  width: 100% !important;',
    '  height: 100% !important;',
    '  object-fit: cover !important;',
    '  display: block !important;',
    '}',
    /* Keep only the primary image visible by default. If the linked
       stylesheet IS present it already handles the hover swap; if it is
       not, this stops the hover image from stacking on top at full opacity
       (both are now absolutely positioned, so this is purely a safeguard). */
    '.tabbed-product-card__img--hover { opacity: 0 !important; }',
    '.tabbed-product-card:hover .tabbed-product-card__img--primary { opacity: 0 !important; }',
    '.tabbed-product-card:hover .tabbed-product-card__img--hover { opacity: 1 !important; }',
    /* Mobile typography: cards in the dropdown are physically half the
       size of PLP cards, so the snippet's default 14px title / 13px
       price feels chunky against the small image. Step everything
       down a notch — mirrors the PLP_GRID_CSS scale used elsewhere. */
    '@media (max-width: 749px) {',
    '  .tabbed-product-card__title {',
    '    font-size: 11px !important;',
    '    font-weight: 500 !important;',
    '    line-height: 1.25 !important;',
    '  }',
    '  .tabbed-product-card__price {',
    '    font-size: 10px !important;',
    '  }',
    '  .tabbed-product-card__price--compare {',
    '    font-size: 10px !important;',
    '  }',
    '  .tabbed-product-card__rating {',
    '    font-size: 11px !important;',
    '  }',
    '  .tabbed-product-card__star svg {',
    '    width: 11px !important;',
    '    height: 11px !important;',
    '  }',
    '  .tabbed-product-card__swatch {',
    '    width: 12px !important;',
    '    height: 12px !important;',
    '  }',
    /* Cap visible swatches at 3 on mobile (cards are narrower in the
       dropdown than on PLP, where 4 fits). The snippet emits two "+N"
       indicators (--mobile sized from products_count - 4, --desktop
       from products_count - 6) — neither matches "show 3" exactly, so
       prefer the --mobile one as the closest approximation; it'll
       under-count by 1 in some cases but reads correctly as "there
       are more". */
    '  .tabbed-product-card__swatch-link:nth-child(n+4) {',
    '    display: none !important;',
    '  }',
    '  .tabbed-product-card__swatch-more--desktop {',
    '    display: none !important;',
    '  }',
    '  .tabbed-product-card__swatch-more--mobile {',
    '    display: inline !important;',
    '    font-size: 9px !important;',
    '    margin-left: 2px !important;',
    '  }',
    /* Image badges (campaign labels, "-30%", "New") — step the type
       down so they read as labels rather than competing with the
       product title. */
    '  .product-badge {',
    '    font-size: 8px !important;',
    '    font-weight: 400 !important;',
    '    padding: 3px 6px !important;',
    '    letter-spacing: 0.02em !important;',
    '  }',
    '  .product-badge--discount {',
    '    font-weight: 500 !important;',
    '  }',
    '}'
  ].join('\n');

  /* CSS inside <relewise-search-bar>'s shadow root.
     Real DOM (confirmed via devtools):
       <div class="rw-search-bar rw-border" part="input">  // flex row
         <input id="search-input" class="rw-search-bar-input">
         <div class="rw-icon"> ... </div>                  // Relewise gives this flex-grow, so it's ~50% of the bar
     The .rw-icon wrapper grows to share the row with the input, which
     is why the actual glyph lands at the bar's centre. We force that
     wrapper to collapse to its content width so the input fills the
     row and the icon sits flush right. */
  var SEARCHBAR_ROOT_CSS = [
    '.rw-search-bar,',
    '[part="input"] {',
    '  width: 100% !important;',
    '  max-width: none !important;',
    '  margin: 0 !important;',
    '  box-sizing: border-box !important;',
    '  display: flex !important;',
    '  flex-direction: row !important;',
    '  align-items: center !important;',
    '  gap: 0 !important;',
    '}',
    /* Input fills the remaining flex space */
    'input.rw-search-bar-input,',
    'input#search-input,',
    'input[type="text"] {',
    '  flex: 1 1 auto !important;',
    '  width: auto !important;',
    '  min-width: 0 !important;',
    '  border: none !important;',
    '  background: transparent !important;',
    '  outline: none !important;',
    '  padding: 0 8px 0 0 !important;',
    '  font-family: inherit !important;',
    '  font-size: inherit !important;',
    '  color: #000 !important;',
    '}',
    'input::placeholder {',
    '  color: #6E6E6E !important;',
    '  opacity: 1 !important;',
    '  font-family: inherit !important;',
    '}',
    /* Collapse the icon wrapper to content width and right-align whatever
       Relewise renders inside it (magnifier in empty state, clear-X
       when typed). This is the actual fix — previous selectors
       (search-icon / searchbar-icon / clear / close) did NOT match
       this wrapper\'s class name, which is just "rw-icon". */
    '.rw-icon {',
    '  flex: 0 0 auto !important;',
    '  width: auto !important;',
    '  min-width: 0 !important;',
    '  max-width: none !important;',
    '  margin: 0 0 0 auto !important;',
    '  padding: 0 !important;',
    '  display: inline-flex !important;',
    '  align-items: center !important;',
    '  justify-content: flex-end !important;',
    '  position: static !important;',
    '}',
    /* The glyph itself — always visible, natural size */
    '.rw-icon > *,',
    '.rw-icon svg,',
    '.rw-icon button,',
    '[part="icon"],',
    '.rw-search-bar-icon {',
    '  display: inline-flex !important;',
    '  opacity: 1 !important;',
    '  visibility: visible !important;',
    '  pointer-events: auto !important;',
    '  color: #000 !important;',
    '  width: 20px !important;',
    '  height: 20px !important;',
    '  flex-shrink: 0 !important;',
    '  position: static !important;',
    '  margin: 0 !important;',
    '}',
    /* Mobile: --relewise-base-font-size doesn\'t reliably reach the
       <input> through `font-size: inherit` (Relewise uses the variable
       only for some internal selectors), so set the size directly on
       the input itself. Also collapse the row to ~36px so the input
       doesn\'t eat the top of the modal. The placeholder is the same
       size as the input text. */
    '@media (max-width: 749px) {',
    '  .rw-search-bar,',
    '  [part="input"] {',
    '    min-height: 40px !important;',
    '    height: 40px !important;',
    '  }',
    '  input.rw-search-bar-input,',
    '  input#search-input,',
    '  input[type="text"] {',
    /* 16px keeps iOS Safari from auto-zooming when the field is focused */
    '    font-size: 16px !important;',
    '    line-height: 1.2 !important;',
    '    height: 40px !important;',
    '  }',
    '  .rw-icon > *,',
    '  .rw-icon svg,',
    '  .rw-icon button,',
    '  [part="icon"],',
    '  .rw-search-bar-icon {',
    '    width: 16px !important;',
    '    height: 16px !important;',
    '  }',
    '}'
  ].join('\n');

  function buildSheet(cssText, cache) {
    if (cache.sheet) return cache.sheet;
    try {
      cache.sheet = new CSSStyleSheet();
      cache.sheet.replaceSync(cssText);
    } catch (_) {
      cache.sheet = null;
    }
    return cache.sheet;
  }
  var overlayCache = {};
  var searchBarCache = {};
  var resultsCache = {};
  var categoryCache = {};
  var productCache = {};

  function adoptInto(root, cssText, cache) {
    if (!root) return;
    var sheet = buildSheet(cssText, cache);
    if (sheet && root.adoptedStyleSheets && root.adoptedStyleSheets.indexOf(sheet) === -1) {
      try {
        root.adoptedStyleSheets = root.adoptedStyleSheets.concat([sheet]);
        return;
      } catch (_) { /* fall through */ }
    }
    if (!root.querySelector('style[data-rw-injected]')) {
      var style = document.createElement('style');
      style.setAttribute('data-rw-injected', '');
      style.textContent = cssText;
      root.appendChild(style);
    }
  }

  function setPlaceholder(input) {
    if (input.getAttribute('placeholder') !== PLACEHOLDER) {
      input.setAttribute('placeholder', PLACEHOLDER);
      input.placeholder = PLACEHOLDER;
    }
  }

  function wireInput(input) {
    if (input.__rwWired) return;
    input.__rwWired = true;
    setPlaceholder(input);
    var mo = new MutationObserver(function () { setPlaceholder(input); });
    mo.observe(input, { attributes: true, attributeFilter: ['placeholder'] });
  }

  /* Localise the overlay's hard-coded English strings into Swedish.
     Relewise has no built-in translation hook for these labels, and
     they re-render on every keystroke, so this runs from the results
     MutationObserver. Each rule is idempotent — we only rewrite when
     the current text matches the English form, so the observer's
     own DOM writes don't trigger a re-translate loop. */
  function translateResults(root) {
    if (!root) return;
    root.querySelectorAll('.rw-title').forEach(function (el) {
      var txt = (el.textContent || '').trim();
      if (/^products$/i.test(txt)) el.textContent = S.products || 'Produkter';
      else if (/^categories$/i.test(txt)) el.textContent = S.categories || 'Kategorier';
    });
    root.querySelectorAll('.rw-selected-result').forEach(function (row) {
      if (!row.querySelector('relewise-arrow-up-icon')) return;
      row.querySelectorAll('.rw-item').forEach(function (item) {
        var txt = (item.textContent || '').trim();
        var m = txt.match(/^show all results\s*(\(\d+\))?$/i);
        if (m) item.textContent = (S.showAllResults || 'Visa alla resultat') + (m[1] ? ' ' + m[1] : '');
      });
    });
    /* Empty-state message ("No products found", etc). Relewise renders
       this text without a stable class, so walk leaf elements and match
       known English phrasings only — keeps the rewrite idempotent. */
    var TEXT_RULES = [
      { from: /^no products found\.?$/i,            to: S.noProducts || 'Inga produkter hittades.' },
      { from: /^no results found\.?$/i,             to: S.noResults  || 'Inga resultat hittades.' },
      { from: /^no results\.?$/i,                   to: S.noResults  || 'Inga resultat.' },
      { from: /^we didn'?t find any products\.?$/i, to: S.noProducts || 'Vi hittade inga produkter.' },
      { from: /^sorry,?\s*no products found\.?$/i,  to: S.noProducts || 'Tyvärr, inga produkter hittades.' }
    ];
    root.querySelectorAll('*').forEach(function (el) {
      if (el.children && el.children.length > 0) return;
      var txt = (el.textContent || '').trim();
      if (!txt) return;
      for (var i = 0; i < TEXT_RULES.length; i++) {
        if (TEXT_RULES[i].from.test(txt)) {
          el.textContent = TEXT_RULES[i].to;
          break;
        }
      }
    });
  }

  function processOverlay(overlay) {
    if (!overlay) return;

    /* Cap the overlay at 4 products. The <relewise-product-search-overlay>
       element is injected by the Relewise app (not present in theme markup),
       and its `number-of-products` attribute defaults to 5 — there is no
       Shopify-admin setting for this. We set it to 4 as soon as the element
       appears so Relewise actually requests four products, instead of just
       hiding the 5th in CSS. The RESULTS_ROOT_CSS rule that hides product
       rows beyond the 4th stays as a belt-and-suspenders fallback in case
       Relewise changes the default again. */
    if (overlay.getAttribute('number-of-products') !== '4') {
      overlay.setAttribute('number-of-products', '4');
    }

    if (!overlay.shadowRoot) return;
    adoptInto(overlay.shadowRoot, OVERLAY_ROOT_CSS, overlayCache);

    var searchBar = overlay.shadowRoot.querySelector('relewise-search-bar');
    if (searchBar && searchBar.shadowRoot) {
      adoptInto(searchBar.shadowRoot, SEARCHBAR_ROOT_CSS, searchBarCache);
      var input = searchBar.shadowRoot.querySelector('input');
      if (input) wireInput(input);
    }

    var results = overlay.shadowRoot.querySelector('relewise-product-search-overlay-results');
    if (results && results.shadowRoot) {
      adoptInto(results.shadowRoot, RESULTS_ROOT_CSS, resultsCache);
      translateResults(results.shadowRoot);
      if (!results.__rwObs) {
        var ro = new MutationObserver(function () {
          adoptInto(results.shadowRoot, RESULTS_ROOT_CSS, resultsCache);
          translateResults(results.shadowRoot);

          /* Inject styling into each category element's shadow root */
          var cats = results.shadowRoot.querySelectorAll('relewise-product-search-overlay-product-category');
          cats.forEach(function (cat) {
            if (cat.shadowRoot) {
              adoptInto(cat.shadowRoot, CATEGORY_ROOT_CSS, categoryCache);
            }
          });

          /* Inject aspect-ratio enforcement into each product element's
             shadow root so card images render at uniform heights. */
          var prods = results.shadowRoot.querySelectorAll('relewise-product-search-overlay-product');
          prods.forEach(function (prod) {
            if (prod.shadowRoot) {
              adoptInto(prod.shadowRoot, PRODUCT_ROOT_CSS, productCache);
            }
          });

          /* DEBUG: dump the shadow DOM of the first product + category
             custom elements once, so we can iterate on their styling. */
          if (!window.__rwProductLogged) {
            var product = results.shadowRoot.querySelector('relewise-product-search-overlay-product');
            if (product && product.shadowRoot) {
              var html = product.shadowRoot.innerHTML || '';
              if (html.length > 100) {
                window.__rwProductLogged = true;
                console.log('[Relewise debug] product element shadow DOM:\n' + html);
              }
            }
          }
          if (!window.__rwCategoryLogged) {
            var cat0 = results.shadowRoot.querySelector('relewise-product-search-overlay-product-category');
            if (cat0 && cat0.shadowRoot) {
              var catHtml = cat0.shadowRoot.innerHTML || '';
              if (catHtml.length > 50) {
                window.__rwCategoryLogged = true;
                console.log('[Relewise debug] category element shadow DOM:\n' + catHtml);
              }
            }
          }
        });
        ro.observe(results.shadowRoot, { childList: true, subtree: true });
        results.__rwObs = ro;
      }
    }

    if (!overlay.__rwInnerObs) {
      var inner = new MutationObserver(function () { processOverlay(overlay); });
      inner.observe(overlay.shadowRoot, { childList: true, subtree: true });
      overlay.__rwInnerObs = inner;
    }
  }

  function sweep() {
    var overlays = document.querySelectorAll('relewise-product-search-overlay');
    overlays.forEach(processOverlay);
    return overlays.length > 0;
  }

  sweep();
  var ticks = 0;
  var poll = setInterval(function () {
    ticks++;
    if (sweep() && ticks > 2) return;
    if (ticks > 80) clearInterval(poll);
  }, 150);

  function bodyObserver() {
    var obs = new MutationObserver(sweep);
    obs.observe(document.body, { childList: true, subtree: true });
  }
  if (document.body) bodyObserver();
  else document.addEventListener('DOMContentLoaded', bodyObserver);
})();
